import { buildSeriesRebasePlan, type EditScope } from './seriesRebase';
import {
    isNthWeekday,
    isWeeklyMultiDay,
    occurrenceDateAt,
    rewindFrom,
    type RecurrenceRuleFields,
} from './recurrence';

/** Rebase de recorrência nunca recebe `single` (a edição de uma ocorrência só vive no nível da transação). */
export type RecurringSeriesRebaseMode = Exclude<EditScope, 'single'>;

export type RecurringSeriesRebaseEntry = {
    occurrenceIndex: number;
    date: string;
    invoiceMonth?: string;
    /**
     * SLOT novo (`recurringOccurrenceDate`) da linha. Ausente = a linha MANTÉM o slot atual —
     * caso do padrão avançado, em que a regra não é re-ancorada e a data editada vira uma
     * "data movida" sobre o slot original.
     */
    slot?: string;
};

/** Cadência da série — o suficiente pra régua de datas (a âncora vem do baseDate do plano). */
export type RecurringSeriesCadence = Pick<
    RecurrenceRuleFields,
    'frequency' | 'interval' | 'weekdays' | 'monthlyMode'
>;

type BuildRecurringSeriesRebasePlanParams = {
    occurrenceCount: number;
    anchorIndex: number;
    mode: RecurringSeriesRebaseMode;
    baseDate: string;
    baseInvoiceMonth?: string;
    /**
     * Cadência da regra. Sem ela (ou mensal simples), o plano usa a régua mensal legada
     * (`addMonths` a partir do baseDate). COM ela e frequência não-mensal, as datas saem do
     * motor de recorrência — uma série SEMANAL editada em "Todas" reescrevia o histórico com
     * espaçamento MENSAL (diarista de junho espalhada por março–junho). Ver auditoria jul/2026.
     */
    cadence?: RecurringSeriesCadence | null;
};

/** Adapter: a posição da série é o índice 0-based da ocorrência num range contíguo `0..count-1`. */
export function buildRecurringSeriesRebasePlan({
    occurrenceCount,
    anchorIndex,
    mode,
    baseDate,
    baseInvoiceMonth,
    cadence,
}: BuildRecurringSeriesRebasePlanParams): RecurringSeriesRebaseEntry[] {
    // Padrão AVANÇADO (semanal multi-dia "ter+qui" / mensal "1º sábado"): não existe rebase
    // posicional bem-definido por um offset arbitrário — o MESMO motivo pelo qual o caller não
    // re-ancora a regra nesses padrões (`isAdvanced` em transactionRepository). Tentar a régua
    // do motor aqui quebrava de verdade: o rewind do walker trava no startDate (= baseDate),
    // não rebobina, e a série INTEIRA saía deslocada `anchorIndex` ocorrências pra frente.
    // Só a âncora recebe a data editada (como numa edição "só esta"); sem `slot` — a linha
    // vira data movida sobre o slot original, coerente com a regra que não mudou de âncora.
    if (cadence && (isWeeklyMultiDay(cadence) || isNthWeekday(cadence))) {
        return [{ occurrenceIndex: anchorIndex, date: baseDate, invoiceMonth: baseInvoiceMonth }];
    }

    const monthlyPlan = buildSeriesRebasePlan({
        positions: Array.from({ length: occurrenceCount }, (_, occurrenceIndex) => occurrenceIndex),
        anchor: anchorIndex,
        mode,
        baseDate,
        baseInvoiceMonth,
    });

    // Mensal simples mantém a régua legada (mesma aritmética, coberta por testes desde o ADR-0010).
    const useEngine = !!cadence && cadence.frequency !== 'monthly';
    if (!useEngine) {
        return monthlyPlan.map((entry) => ({
            occurrenceIndex: entry.position,
            date: entry.date,
            invoiceMonth: entry.invoiceMonth,
            slot: entry.date,
        }));
    }

    // Régua do motor: ancora uma regra sintética no baseDate (rebobina até a posição 0) e
    // projeta cada posição. `invoiceMonth` fica de fora de propósito: competência é mensal;
    // pra cadência não-mensal o chamador deriva da DATA nova (fechamento do cartão) — deslizar
    // por offset de posição colocaria duas ocorrências da mesma semana em meses diferentes.
    const anchored: RecurrenceRuleFields = { ...cadence, startDate: baseDate };
    const walker: RecurrenceRuleFields = {
        ...cadence,
        startDate: rewindFrom(baseDate, anchorIndex, anchored),
    };
    return monthlyPlan.map((entry) => {
        const date = occurrenceDateAt(entry.position, walker);
        return { occurrenceIndex: entry.position, date, invoiceMonth: undefined, slot: date };
    });
}
